import { Briefcase, Check, Landmark, Plug, Warehouse } from 'lucide-react';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import { Heading, Lead } from '@/Components/Marketing/Typography';

/**
 * The four kinds of partner, in order, each with its icon. Home's own list
 * and copy (`site.home.partners.<key>.*`), not the Partnerships page's
 * module: one page's sections never go into another page's chunks, and that
 * page has the full points. Every point is a capability the product has
 * (the claims ledger); none claims partner access to a client's data.
 */
export const PARTNER_TYPES = Object.freeze([
    { key: 'consultancies', Icon: Briefcase },
    { key: 'software', Icon: Plug },
    { key: 'associations', Icon: Landmark },
    { key: 'resellers', Icon: Warehouse },
]);

/** How many points each partner type shows on Home. */
export const HOME_POINTS = 2;

/**
 * Home, after the product: the partner program (2026-10-04). The four kinds
 * of partner (wholesalers and resellers joined consultancies, software and
 * ERP vendors and industry associations on 2026-10-05), each with two of
 * the things Synex Cloud gives them, and the way in: the partner form, or
 * the page. Four across from 1280px, two by two from 768px, one column on a
 * phone, so the set stays even at every width.
 */
export default function PartnerProgram() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const headingId = useId();
    const page = route('outer.partnerships');

    return (
        <Section tone="surface" aria-labelledby={headingId} className="border-t border-mk-line">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end lg:gap-16">
                <Reveal className="lg:col-span-7">
                    <Heading id={headingId} className="max-w-[14em]">{t('site.home.partners.title')}</Heading>
                    <Lead className="mt-6 max-w-[46ch]">{t('site.home.partners.lead')}</Lead>
                </Reveal>
                <Reveal delay={0.06} className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
                    <Button href={`${page}#partner-form`} size="lg">{t('site.partnerships.hero.cta')}</Button>
                    <Button href={page} variant="secondary" size="lg">{t('site.home.partners.link')}</Button>
                </Reveal>
            </div>

            <ul role="list" className="mt-14 grid grid-cols-1 gap-4 sm:mt-16 md:grid-cols-2 xl:grid-cols-4">
                {PARTNER_TYPES.map(({ key, Icon }, index) => (
                    <Reveal as="li" key={key} delay={(index % 2) * 0.06} className="flex">
                        <article data-audience={key} className="flex w-full flex-col rounded-mk-card bg-mk-canvas p-6 ring-1 ring-inset ring-mk-line sm:p-8 xl:p-6">
                            <span className="inline-flex size-10 items-center justify-center rounded-lg bg-mk-orange-soft text-mk-orange">
                                <Icon aria-hidden="true" strokeWidth={1.5} className="size-5" />
                            </span>
                            <h3 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-balance text-mk-ink">{t(`site.home.partners.${key}.title`)}</h3>
                            <p className="mb-6 mt-3 text-[15px] leading-relaxed text-pretty text-mk-body">{t(`site.home.partners.${key}.text`)}</p>
                            {/* The points sit at the card's foot, level across a row whatever the sentence above runs to. */}
                            <ul role="list" className="mt-auto grid gap-3 border-t border-mk-line pt-6">
                                {Array.from({ length: HOME_POINTS }, (_, point) => (
                                    <li key={point} className="flex gap-3 text-[15px] leading-relaxed text-mk-ink">
                                        <Check aria-hidden="true" strokeWidth={1.5} className="mt-1 size-4 shrink-0 text-mk-orange" />
                                        <span>{t(`site.home.partners.${key}.point${point + 1}`)}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    </Reveal>
                ))}
            </ul>
        </Section>
    );
}
